<template>
  <q-card class="deal-score-card">
    <q-card-section v-if="loading" class="q-pa-lg">
      <div class="column q-gutter-y-md">
        <q-skeleton height="100px" animation="fade" />
        <q-skeleton height="200px" animation="fade" />
        <q-skeleton height="150px" animation="fade" />
      </div>
    </q-card-section>

    <div v-else-if="dealScoreData">
      <q-card-section class="flex items-center no-wrap">
        <ScoreCircle
          :rating="dealScoreData.score"
          size="60px"
          style="margin-bottom: -2px"
        />
        <div class="q-ml-md">
          <h4 class="q-mb-xs">
            This vehicle is a
            <span
              class="text-weight-bold"
              :style="{ color: convertScoreToColor(dealScoreData.score) }"
              >{{ convertScoreToDealPhrase(dealScoreData.score) }}</span
            >.
          </h4>
          <p class="text-text-light q-mb-none">
            Deal Score: {{ dealScoreData.score }}/100
          </p>
        </div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div v-if="flags.length > 0" class="deal-score-overview">
          <div class="deal-score-flags">
            <HighlightBadge
              v-for="(flag, i) in flags"
              :key="i"
              :color="flag.color"
              :icon="flag.icon"
            >
              <div class="q-ml-sm">{{ flag.text }}</div>
            </HighlightBadge>
          </div>
        </div>

        <div class="deal-score-components q-pb-sm">
          <div class="component-bars">
            <div
              v-for="(value, key) in dealScoreData.components"
              :key="key"
              class="component-bar-item"
            >
              <div class="component-bar-label">
                <div class="flex items-center">
                  <span class="text-capitalize text-weight-medium">{{
                    key == "risk"
                      ? "Buyer Risk"
                      : key == "motivation"
                      ? "Seller Motivation"
                      : key
                  }}</span>
                  <span class="q-mx-sm">-</span>
                  <span
                    class="component-phrase text-capitalize"
                    :style="{ color: convertScoreToColor(value, true) }"
                  >
                    {{
                      key == "risk"
                        ? convertScoreToHighLowInvertedPhrase(value)
                        : convertScoreToPhrase(value, true)
                    }}
                  </span>
                </div>
                <span class="text-weight-bold --text-md"
                  >{{ Math.round(value) }}
                  <small class="text-text-light q-ml-xs">/ 100</small></span
                >
              </div>
              <div class="component-bar-track">
                <div
                  class="component-bar-fill"
                  :style="{
                    width: value + '%',
                    background: convertScoreToColor(value, true)
                  }"
                />
              </div>
            </div>
          </div>
        </div>

        <q-separator class="q-my-lg" />

        <div class="market-distribution">
          <div class="flex items-center justify-between q-mb-md">
            <div class="flex items-center">
              <q-icon name="sym_r_insights" size="27px" class="q-mr-sm" />
              <h5 class="q-mb-none">Market Insights</h5>
            </div>
            <div
              class="text-text-light flex items-center"
              style="font-size: 12px;"
            >
              Updated {{ formatDate(vehicleMarket.time_updated) }}
            </div>
          </div>
          <div class="chart-container">
            <svg
              :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
              class="price-chart"
            >
              <!-- Shadow filter for price marker -->
              <defs>
                <filter
                  id="shadow-bottom"
                  x="-50%"
                  y="-50%"
                  width="200%"
                  height="200%"
                >
                  <feDropShadow
                    dx="0"
                    dy="2"
                    stdDeviation="2"
                    flood-opacity="0.3"
                  />
                </filter>
              </defs>

              <!-- Base line (x-axis) -->
              <line
                :x1="paddingLeft"
                :y1="chartHeight - paddingBottom"
                :x2="chartWidth - paddingRight"
                :y2="chartHeight - paddingBottom"
                stroke="#3a3d3f"
                stroke-width="1.5"
              />

              <!-- Draw filled curve sections with solid colors -->
              <!-- (left/low prices) -->
              <path
                :d="getCurveSectionPath(0, stdDevPositions.minusOneStd)"
                fill="#037933"
                opacity="0.85"
              />

              <!-- Green section left half (middle/average prices - darker green) -->
              <path
                :d="
                  getCurveSectionPath(
                    stdDevPositions.minusOneStd,
                    stdDevPositions.avg
                  )
                "
                fill="#0C9A45"
                opacity="0.85"
              />

              <!-- Green section right half (middle/average prices - lighter green) -->
              <path
                :d="
                  getCurveSectionPath(
                    stdDevPositions.avg,
                    stdDevPositions.plusOneStd
                  )
                "
                fill="#28AE5E"
                opacity="0.85"
              />

              <!-- (right/high prices) -->
              <path
                :d="getCurveSectionPath(stdDevPositions.plusOneStd, 100)"
                fill="#70D498"
                opacity="0.85"
              />

              <!-- Draw curve outline -->
              <path
                :d="curvePath"
                fill="none"
                stroke="#2a2d2e"
                stroke-width="1"
              />

              <!-- Expected price marker (vertical line and dot) - no label on curve -->
              <g v-if="priceMarkers.expected">
                <line
                  :x1="priceMarkers.expected.x"
                  :y1="priceMarkers.expected.y"
                  :x2="priceMarkers.expected.x"
                  :y2="chartHeight - paddingBottom"
                  stroke="#ffffff"
                  stroke-width="3"
                />
                <circle
                  :cx="priceMarkers.expected.x"
                  :cy="priceMarkers.expected.y"
                  r="5"
                  fill="#ffffff"
                  stroke="#1a1d1e"
                  stroke-width="3"
                  filter="url(#shadow-bottom)"
                />
              </g>

              <!-- Vehicle price marker (this listing's price) - with label on curve only -->
              <g v-if="priceMarkers.vehicle">
                <line
                  :x1="priceMarkers.vehicle.x"
                  :y1="priceMarkers.vehicle.y"
                  :x2="priceMarkers.vehicle.x"
                  :y2="chartHeight - paddingBottom"
                  stroke="#E7171B"
                  stroke-width="3"
                />
                <circle
                  :cx="priceMarkers.vehicle.x"
                  :cy="priceMarkers.vehicle.y"
                  r="5"
                  fill="#E7171B"
                  stroke="#1a1d1e"
                  stroke-width="3"
                  filter="url(#shadow-bottom)"
                />
                <!-- Label with background - speech bubble style with dynamic positioning -->
                <path
                  :d="
                    `M ${priceMarkers.vehicle.x -
                      42 +
                      vehicleLabelPosition.offsetX} ${priceMarkers.vehicle.y -
                      57}
                       h 84
                       a 8 8 0 0 1 8 8
                       v 24
                       a 8 8 0 0 1 -8 8
                       h ${-34 - vehicleLabelPosition.offsetX}
                       l -8 10
                       l -8 -10
                       h ${-34 + vehicleLabelPosition.offsetX}
                       a 8 8 0 0 1 -8 -8
                       v -24
                       a 8 8 0 0 1 8 -8 z`
                  "
                  fill="#2c2f31"
                  stroke="#3a3d3f"
                  stroke-width="1"
                  filter="url(#shadow-bottom)"
                />
                <!-- Price phrase (colored) -->
                <text
                  :x="priceMarkers.vehicle.x + vehicleLabelPosition.offsetX"
                  :y="priceMarkers.vehicle.y - 45"
                  text-anchor="middle"
                  class="chart-price-phrase"
                  :fill="convertScoreToColor(dealScoreData.score)"
                >
                  {{ convertScoreToPhrase(dealScoreData.score) }} Deal
                </text>
                <!-- Price value -->
                <text
                  :x="priceMarkers.vehicle.x + vehicleLabelPosition.offsetX"
                  :y="priceMarkers.vehicle.y - 26"
                  text-anchor="middle"
                  class="chart-price-label"
                  fill="#ffffff"
                >
                  ${{ formatNumber(price) }}
                </text>
              </g>

              <!-- X-axis labels: Min, Avg, Max -->
              <text
                :x="paddingLeft"
                :y="chartHeight - paddingBottom + 25"
                text-anchor="start"
                class="chart-axis-label"
              >
                ${{ formatNumber(adjustedMinPrice, true, 1) }}
              </text>
              <text
                :x="priceMarkers.avg.x"
                :y="chartHeight - paddingBottom + 25"
                text-anchor="middle"
                class="chart-axis-label"
              >
                ${{ formatNumber(vehicleMarket.avg_price, true, 1) }}
              </text>
              <text
                :x="chartWidth - paddingRight"
                :y="chartHeight - paddingBottom + 25"
                text-anchor="end"
                class="chart-axis-label"
              >
                ${{ formatNumber(adjustedMaxPrice, true, 1) }}
              </text>

              <!-- Expected price x-axis marker with label -->
              <g v-if="priceMarkers.expected">
                <line
                  :x1="priceMarkers.expected.x"
                  :y1="chartHeight - paddingBottom"
                  :x2="priceMarkers.expected.x"
                  :y2="chartHeight - paddingBottom + 25"
                  stroke="#ffffff"
                  stroke-width="2"
                />
                <text
                  :x="priceMarkers.expected.x"
                  :y="chartHeight - paddingBottom + 45"
                  text-anchor="middle"
                  class="chart-axis-label chart-axis-label--expected"
                  fill="#ffffff"
                >
                  Expected Price - ${{
                    formatNumber(dealScoreData.expected_price, true, 1)
                  }}
                </text>
              </g>
            </svg>
          </div>

          <!-- Market Insights -->
          <div class="market-insights q-mt-md q-mb-sm">
            <div class="row q-col-gutter-md">
              <!-- Price Stats Card -->
              <div class="col-12 col-md-6">
                <div class="insight-card">
                  <h6 class="q-mb-sm">
                    Price
                    <span style="text-transform: lowercase !important;"
                      >vs</span
                    >
                    Market
                  </h6>
                  <div class="insight-grid">
                    <div class="insight-item">
                      <small class="text-text-light">Vehicle Price</small>
                      <p class="text-weight-bold q-mb-none">
                        ${{ formatNumber(price) }}
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Expected Price
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            Calculated by taking the average market price and
                            adjusting it based on this vehicle's mileage. This
                            gives you a fair-market estimate of what this
                            specific vehicle should cost.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        ${{ formatNumber(dealScoreData.expected_price) }}
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Value Gap
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            The difference between the vehicle's listed price
                            and the expected price. A positive value (in green)
                            means the vehicle is priced below expected.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p
                        class="text-weight-bold q-mb-none"
                        :class="
                          dealScoreData.value_gap < 0
                            ? 'text-negative'
                            : 'text-positive'
                        "
                      >
                        {{ dealScoreData.value_gap &lt; 0 ? "-" : "+" }}${{
                          formatNumber(Math.abs(dealScoreData.value_gap))
                        }}
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Market Average
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            The average price of all similar vehicles currently
                            listed in the market.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        ${{ formatNumber(Math.round(vehicleMarket.avg_price)) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mileage Metrics Card -->
              <div class="col-12 col-md-6">
                <div class="insight-card">
                  <h6 class="q-mb-sm">
                    Mileage
                    <span style="text-transform: lowercase !important;"
                      >vs</span
                    >
                    Market
                  </h6>
                  <div class="insight-grid">
                    <div class="insight-item">
                      <small class="text-text-light">Vehicle Mileage</small>
                      <p class="text-weight-bold q-mb-none">
                        {{ formatNumber(mileage) }} mi
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Market Average
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            The average mileage of all similar vehicles
                            currently listed in the market.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        {{
                          formatNumber(Math.round(vehicleMarket.avg_mileage))
                        }}
                        mi
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Mileage vs Avg
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            The difference between this vehicle's mileage and
                            the market average. A negative value (in green)
                            means lower mileage, which is better.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p
                        class="text-weight-bold q-mb-none"
                        :class="mileageVsAvg &lt; 0 ? 'text-positive' : 'text-negative'"
                      >
                        {{ mileageVsAvg &gt; 0 ? '+' : ''



















                        }}{{ formatNumber(mileageVsAvg) }} mi
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Mileage Rating
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            A rating based on how this vehicle's mileage
                            compares to the market average.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p
                        class="text-weight-bold q-mb-none"
                        :style="{ color: mileageRatingColor }"
                      >
                        {{ mileageRating }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Market Distribution Card -->
              <div class="col-12">
                <div class="insight-card">
                  <h6 class="q-mb-sm">Market Distribution</h6>
                  <div class="insight-grid insight-grid--4col">
                    <div class="insight-item">
                      <small class="text-text-light">Total Vehicles</small>
                      <p class="text-weight-bold q-mb-none">
                        {{ formatNumber(vehicleMarket.total_vehicles) }}
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Market Spread
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            The difference between the highest and lowest priced
                            vehicles in the market. A larger spread indicates
                            more price variation.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        ${{ formatNumber(marketSpread, true, 1) }}
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Price Volatility
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            A measure of price variation in the market. Higher
                            percentages indicate more inconsistent pricing.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        {{ priceVolatility }}%
                      </p>
                    </div>
                    <div class="insight-item">
                      <small class="text-text-light">
                        Competitiveness
                        <q-icon
                          name="sym_r_info"
                          size="14px"
                          style="margin-top: -2px;"
                        >
                          <q-tooltip max-width="300px">
                            How competitive this market is based on total
                            vehicles, price spread, and volatility. Higher
                            competition means more choices for buyers.
                          </q-tooltip>
                        </q-icon>
                      </small>
                      <p class="text-weight-bold q-mb-none">
                        {{ competitivenessLabel }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </q-card-section>
    </div>

    <q-card-section v-else class="text-center q-pa-lg">
      <q-icon
        name="sym_r_bar_chart_off"
        size="54px"
        class="text-text-light q-mb-md"
      />
      <h6 class="q-mb-xs">Deal Score Not Available</h6>
      <p class="text-text-light --text-sm">
        We're still gathering data on this vehicle's market, check back soon.
      </p>
    </q-card-section>
  </q-card>
</template>

<script>
import axios from "axios";
import ScoreCircle from "@/components/UI/ScoreCircle.vue";
import HighlightBadge from "@/components/UI/HighlightBadge.vue";
import dayjs from "dayjs";

export default {
  name: "VehicleDealScoreDetails",
  components: {
    ScoreCircle,
    HighlightBadge
  },
  props: {
    productId: {
      type: String,
      required: true
    },
    price: {
      type: [Number, String],
      required: false,
      default: null
    },
    mileage: {
      type: [Number, String],
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      dealScoreData: null,
      vehicleMarket: null,
      chartWidth: 600,
      chartHeight: this.windowWidth < 769 ? 500 : 200,
      paddingTop: 8,
      paddingBottom: 36,
      paddingLeft: 16,
      paddingRight: 16
    };
  },
  computed: {
    flags() {
      if (!this.dealScoreData) return [];

      const positiveFlags = this.dealScoreData.positive_flags.map(text => ({
        text,
        color: "positive",
        icon: "sym_r_thumb_up"
      }));

      const negativeFlags = this.dealScoreData.negative_flags.map(text => ({
        text,
        color: "negative",
        icon: "sym_r_thumb_down"
      }));

      const neutralFlags = (this.dealScoreData.neutral_flags || []).map(
        text => ({
          text,
          color: "gray",
          icon: "sym_r_campaign"
        })
      );

      return [...positiveFlags, ...negativeFlags, ...neutralFlags];
    },
    stdDevPositions() {
      if (!this.vehicleMarket) return {};

      // Use adjusted min/max for consistency
      const minPrice = this.adjustedMinPrice;
      const maxPrice = this.adjustedMaxPrice;
      const avgPrice = parseFloat(this.vehicleMarket.avg_price);
      const stdDev = parseFloat(this.vehicleMarket.price_std_dev);
      const priceRange = maxPrice - minPrice;

      const getPricePercentage = price => {
        return ((price - minPrice) / priceRange) * 100;
      };

      return {
        minusTwoStd: getPricePercentage(
          Math.max(minPrice, avgPrice - 2 * stdDev)
        ),
        minusOneStd: getPricePercentage(
          Math.max(minPrice, avgPrice - 1 * stdDev)
        ),
        avg: getPricePercentage(avgPrice),
        plusOneStd: getPricePercentage(
          Math.min(maxPrice, avgPrice + 1 * stdDev)
        ),
        plusTwoStd: getPricePercentage(
          Math.min(maxPrice, avgPrice + 2 * stdDev)
        )
      };
    },
    curvePath() {
      if (!this.vehicleMarket) return "";

      const points = this.generateCurvePoints();
      if (points.length === 0) return "";

      let path = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        path += ` L ${points[i].x} ${points[i].y}`;
      }

      return path;
    },
    curvePathFilled() {
      if (!this.vehicleMarket) return "";

      const points = this.generateCurvePoints();
      if (points.length === 0) return "";

      let path = `M ${this.padding} ${this.chartHeight - this.padding}`;
      path += ` L ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        path += ` L ${points[i].x} ${points[i].y}`;
      }
      path += ` L ${this.chartWidth - this.padding} ${this.chartHeight -
        this.padding} Z`;

      return path;
    },
    priceMarkers() {
      if (!this.vehicleMarket) return {};

      // Use the adjusted min/max values from computed properties
      const minPrice = this.adjustedMinPrice;
      const maxPrice = this.adjustedMaxPrice;

      const priceRange = maxPrice - minPrice;
      const chartRange = this.chartWidth - this.paddingLeft - this.paddingRight;

      const getX = price => {
        const ratio = (parseFloat(price) - minPrice) / priceRange;
        return this.paddingLeft + ratio * chartRange;
      };

      const getYForPrice = price => {
        const avgPrice = parseFloat(this.vehicleMarket.avg_price);
        const stdDev = parseFloat(this.vehicleMarket.price_std_dev);
        const z = (parseFloat(price) - avgPrice) / stdDev;
        const normalValue =
          Math.exp(-0.5 * z * z) / (stdDev * Math.sqrt(2 * Math.PI));
        const maxHeight =
          (this.chartHeight - this.paddingTop - this.paddingBottom) * 0.75;
        const peakValue = 1 / (stdDev * Math.sqrt(2 * Math.PI));
        const heightRatio = normalValue / peakValue;
        return this.chartHeight - this.paddingBottom - maxHeight * heightRatio;
      };

      const markers = {
        avg: { x: getX(this.vehicleMarket.avg_price) }
      };

      // Add expected price marker if available
      if (this.dealScoreData && this.dealScoreData.expected_price) {
        const expectedPrice = parseFloat(this.dealScoreData.expected_price);
        markers.expected = {
          x: getX(expectedPrice),
          y: getYForPrice(expectedPrice)
        };
      }

      // Add vehicle price marker if available
      if (this.price) {
        const vehiclePrice = parseFloat(this.price);
        markers.vehicle = {
          x: getX(vehiclePrice),
          y: getYForPrice(vehiclePrice)
        };
      }

      return markers;
    },
    adjustedMinPrice() {
      if (!this.vehicleMarket) return 0;

      const avgPrice = parseFloat(this.vehicleMarket.avg_price);
      const stdDev = parseFloat(this.vehicleMarket.price_std_dev);

      // Use 2.5 std deviations as a reasonable trim point (covers ~98% of data)
      // This creates a cleaner graph while still showing most of the distribution
      let minPrice = Math.max(
        parseFloat(this.vehicleMarket.min_price),
        avgPrice - 3 * stdDev
      );

      // Expand range if expected price or vehicle price falls outside
      // Add 5% buffer to prevent edge markers from being cut off
      if (this.dealScoreData && this.dealScoreData.expected_price) {
        const expectedPrice = parseFloat(this.dealScoreData.expected_price);
        if (expectedPrice < minPrice) {
          const buffer = stdDev * 0.3; // Add small buffer
          minPrice = expectedPrice - buffer;
        }
      }

      if (this.price) {
        const vehiclePrice = parseFloat(this.price);
        if (vehiclePrice < minPrice) {
          const buffer = stdDev * 0.3; // Add small buffer
          minPrice = vehiclePrice - buffer;
        }
      }

      return minPrice;
    },
    adjustedMaxPrice() {
      if (!this.vehicleMarket) return 0;

      const avgPrice = parseFloat(this.vehicleMarket.avg_price);
      const stdDev = parseFloat(this.vehicleMarket.price_std_dev);

      // Use 2.5 std deviations as a reasonable trim point
      let maxPrice = Math.min(
        parseFloat(this.vehicleMarket.max_price),
        avgPrice + 3 * stdDev
      );

      // Expand range if expected price or vehicle price falls outside
      // Add 5% buffer to prevent edge markers from being cut off
      if (this.dealScoreData && this.dealScoreData.expected_price) {
        const expectedPrice = parseFloat(this.dealScoreData.expected_price);
        if (expectedPrice > maxPrice) {
          const buffer = stdDev * 0.3; // Add small buffer
          maxPrice = expectedPrice + buffer;
        }
      }

      if (this.price) {
        const vehiclePrice = parseFloat(this.price);
        if (vehiclePrice > maxPrice) {
          const buffer = stdDev * 0.3; // Add small buffer
          maxPrice = vehiclePrice + buffer;
        }
      }

      return maxPrice;
    },
    vehicleLabelPosition() {
      if (!this.priceMarkers.vehicle) return { offsetX: 0 };

      const bubbleWidth = 84;
      const markerX = this.priceMarkers.vehicle.x;
      const chartLeft = this.paddingLeft;
      const chartRight = this.chartWidth - this.paddingRight;

      // Check if bubble would overflow on the left
      if (markerX - bubbleWidth / 2 < chartLeft) {
        const overflow = chartLeft - (markerX - bubbleWidth / 2);
        return { offsetX: overflow - 28 };
      }

      // Check if bubble would overflow on the right
      if (markerX + bubbleWidth / 2 > chartRight) {
        const overflow = markerX + bubbleWidth / 2 - chartRight;
        return { offsetX: -overflow - 28 };
      }

      return { offsetX: 0 };
    },
    pricePercentile() {
      if (!this.price || !this.vehicleMarket) return 0;

      const minPrice = parseFloat(this.vehicleMarket.min_price);
      const maxPrice = parseFloat(this.vehicleMarket.max_price);
      const currentPrice = parseFloat(this.price);

      const percentile = Math.round(
        ((currentPrice - minPrice) / (maxPrice - minPrice)) * 100
      );
      return Math.max(1, Math.min(99, percentile));
    },
    marketSpread() {
      if (!this.vehicleMarket) return 0;
      return (
        parseFloat(this.vehicleMarket.max_price) -
        parseFloat(this.vehicleMarket.min_price)
      );
    },
    priceVolatility() {
      if (!this.vehicleMarket) return 0;
      const stdDev = parseFloat(this.vehicleMarket.price_std_dev);
      const avgPrice = parseFloat(this.vehicleMarket.avg_price);
      return Math.round((stdDev / avgPrice) * 100);
    },
    competitivenessLabel() {
      if (!this.vehicleMarket) return "N/A";

      const totalVehicles = parseFloat(this.vehicleMarket.total_vehicles);
      const volatility = this.priceVolatility;
      const spread = this.marketSpread;
      const avgPrice = parseFloat(this.vehicleMarket.avg_price);

      // Calculate market competitiveness score
      // High competition = many vehicles + high volatility + large spread relative to average
      const vehicleScore = Math.min(totalVehicles / 10, 40); // Max 40 points, 1 vehicle = 4 points
      const volatilityScore = Math.min(volatility / 2, 30); // Max 30 points
      const spreadScore = Math.min((spread / avgPrice) * 100, 30); // Max 30 points based on spread relative to avg

      const competitivenessScore = vehicleScore + volatilityScore + spreadScore;

      if (competitivenessScore >= 70) return "Very High";
      if (competitivenessScore >= 50) return "High";
      if (competitivenessScore >= 30) return "Moderate";
      return "Low";
    },
    competitivenessColor() {
      const label = this.competitivenessLabel;
      if (label === "Very High") return "#28AE5E";
      if (label === "High") return "#0C9A45";
      if (label === "Moderate") return "#f1c40f";
      return "#ef4444";
    },
    mileageVsAvg() {
      if (!this.mileage || !this.vehicleMarket) return 0;
      return Math.round(
        parseFloat(this.mileage) - parseFloat(this.vehicleMarket.avg_mileage)
      );
    },
    mileageRating() {
      if (!this.mileage || !this.vehicleMarket) return "N/A";

      const currentMileage = parseFloat(this.mileage);
      const avgMileage = parseFloat(this.vehicleMarket.avg_mileage);
      const deviation = (currentMileage - avgMileage) / avgMileage;

      if (deviation <= -0.3) return "Excellent";
      if (deviation <= -0.1) return "Good";
      if (deviation <= 0.1) return "Average";
      if (deviation <= 0.3) return "Below Average";
      return "Poor";
    },
    mileageRatingColor() {
      const rating = this.mileageRating;
      if (rating === "Excellent") return "#28AE5E";
      if (rating === "Good") return "#0C9A45";
      if (rating === "Average") return "#f1c40f";
      if (rating === "Below Average") return "#ef4444";
      return "#ef4444";
    }
  },
  mounted() {
    this.fetchDealScoreDetails();
  },
  methods: {
    generateCurvePoints() {
      if (!this.vehicleMarket) return [];

      const points = [];
      const numPoints = 100;
      const avgPrice = parseFloat(this.vehicleMarket.avg_price);
      const stdDev = parseFloat(this.vehicleMarket.price_std_dev);
      // Use adjusted min/max instead of market min/max
      const minPrice = this.adjustedMinPrice;
      const maxPrice = this.adjustedMaxPrice;
      const priceRange = maxPrice - minPrice;
      const chartRange = this.chartWidth - this.paddingLeft - this.paddingRight;
      const maxHeight =
        (this.chartHeight - this.paddingTop - this.paddingBottom) * 0.75;

      for (let i = 0; i <= numPoints; i++) {
        const ratio = i / numPoints;
        const price = minPrice + priceRange * ratio;
        const x = this.paddingLeft + ratio * chartRange;

        // Calculate normal distribution value
        const z = (price - avgPrice) / stdDev;
        const normalValue =
          Math.exp(-0.5 * z * z) / (stdDev * Math.sqrt(2 * Math.PI));
        const peakValue = 1 / (stdDev * Math.sqrt(2 * Math.PI));
        const heightRatio = normalValue / peakValue;
        const y =
          this.chartHeight - this.paddingBottom - maxHeight * heightRatio;

        points.push({ x, y });
      }

      return points;
    },
    getCurveSectionPath(startPercent, endPercent) {
      const points = this.generateCurvePoints();
      if (points.length === 0) return "";

      const startIndex = Math.floor((startPercent / 100) * points.length);
      const endIndex = Math.ceil((endPercent / 100) * points.length);
      const sectionPoints = points.slice(startIndex, endIndex);

      if (sectionPoints.length === 0) return "";

      // Start from bottom left of section
      let path = `M ${sectionPoints[0].x} ${this.chartHeight -
        this.paddingBottom}`;

      // Go up to curve
      path += ` L ${sectionPoints[0].x} ${sectionPoints[0].y}`;

      // Follow the curve
      for (let i = 1; i < sectionPoints.length; i++) {
        path += ` L ${sectionPoints[i].x} ${sectionPoints[i].y}`;
      }

      // Go down to bottom
      path += ` L ${sectionPoints[sectionPoints.length - 1].x} ${this
        .chartHeight - this.paddingBottom}`;

      // Close path
      path += " Z";

      return path;
    },
    fetchDealScoreDetails() {
      this.loading = true;
      axios
        .get(
          `https://api-v2.jumpfeed.com/network/publisher/products/vehicles/deal-score-details?product_id=${this.productId}`
        )
        .then(response => {
          if (response.data && response.data.data) {
            this.dealScoreData = response.data.data.deal_score_details;
            if (this.dealScoreData) {
              this.dealScoreData.expected_price = Math.round(
                this.dealScoreData.expected_price
              );
              this.dealScoreData.value_gap = Math.round(
                this.dealScoreData.value_gap
              );
            }
            this.vehicleMarket = response.data.data.vehicle_market;
          }
          this.loading = false;
        })
        .catch(error => {
          console.error("Error fetching deal score details:", error);
          this.loading = false;
        });
    },
    formatNumber(x, abbreviated = false, abbreviatedDecimalPlaces = 0) {
      let formattedNumber = x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      if (abbreviated) {
        let abbreviation = "k";
        if (x >= 1000000) {
          abbreviation = "M";
        } else if (x >= 10000000) {
          abbreviation = "B";
        }

        if (
          formattedNumber.substring(0, formattedNumber.indexOf(",")).length > 0
        ) {
          let integerPart = formattedNumber.substring(
            0,
            formattedNumber.indexOf(",")
          );
          let fractionalPart = "";
          if (abbreviatedDecimalPlaces > 0) {
            let decimalValues = formattedNumber.substring(
              formattedNumber.indexOf(",") + 1,
              formattedNumber.indexOf(",") + 1 + abbreviatedDecimalPlaces
            );

            fractionalPart = "." + decimalValues;
          }
          formattedNumber = integerPart + fractionalPart + abbreviation;
        }
      }
      return formattedNumber;
    },
    formatDate(dateString) {
      return dayjs(dateString).format("MMM D, YYYY");
    },
    getComponentColor(value) {
      if (value < 41) return "#e74c3c";
      if (value < 71) return "#f1c40f";
      return "#27ae60";
    }
  }
};
</script>

<style lang="scss" scoped>
.deal-score-card {
  background: color(background, light);
  border: 1px solid color(border);

  .deal-score-overview {
    .deal-score-main {
      display: flex;
      align-items: center;
      gap: $space-lg;
    }

    .deal-score-flags {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 8px;
      margin-bottom: 16px;
    }
  }

  .deal-score-components {
    h6 {
      font-weight: 700;
      margin-bottom: $space-md;
    }

    .component-bars {
      display: flex;
      flex-direction: column;
      gap: $space-md;

      .component-bar-item {
        .component-bar-label {
          display: flex;
          justify-content: space-between;
          margin-bottom: $space-xs;
          font-size: 14px;

          .component-phrase {
            font-size: 13px;
            font-weight: 700;
          }
        }

        .component-bar-track {
          width: 100%;
          height: 8px;
          background: color(background, very-light);
          border-radius: 4px;
          overflow: hidden;

          .component-bar-fill {
            height: 100%;
            border-radius: 4px;
            transition: width 0.3s ease;
          }
        }
      }
    }
  }

  .market-distribution {
    h6 {
      font-weight: 700;
    }

    .chart-container {
      width: 100%;
      background: color(background, medium-light);
      border-radius: 8px;
      padding: $space-lg;
      border: 1px solid #2a2d2e;
      box-shadow: $shadow-bottom;

      .price-chart {
        width: 100%;
        height: auto;
        overflow: visible;

        .chart-axis-label {
          font-size: 13px;
          font-weight: 600;
          fill: #9ca3af;

          &--expected {
            fill: #ffffff;
            font-weight: 700;
          }

          &--vehicle {
            fill: color(secondary);
            font-weight: 700;
          }
        }

        .chart-price-label {
          font-size: 16px;
          font-weight: 900;
        }

        .chart-price-phrase {
          font-size: 9px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }

    .market-insights {
      .insight-card {
        padding: $space-md;
        background: color(background, very-dark);
        border-radius: 8px;
        border: 1px solid color(border);
        box-shadow: $shadow-bottom;

        &--condensed {
          padding: $space-md;
        }

        h6 {
          font-size: 13px;
          font-weight: 700;
          margin-bottom: $space-sm;
          color: color(text);
          letter-spacing: 0.3px;
        }

        .insight-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: $space-md;

          &--4col {
            grid-template-columns: repeat(4, 1fr);

            @media (max-width: 768px) {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          .insight-item {
            small {
              font-size: 12px;
              display: block;
              margin-bottom: 2px;
              font-weight: 600;
            }

            p {
              font-size: 18px;
            }
          }
        }

        small {
          font-size: 11px;
          display: block;
          margin-bottom: 4px;
        }
      }
    }
  }
}
</style>
